import React, { useState, useEffect } from 'react'
import { ChatBubbleLeftRightIcon, CheckCircleIcon, StarIcon } from '@heroicons/react/24/outline'
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid'
import SEO from '@components/common/SEO'
import { useAuth } from '../contexts/AuthContext'
import { feedbackService } from '../services/feedbackService'
import { CreateFeedbackRequest, FeedbackCategory, Feedback } from '../types/feedback'
import toast from 'react-hot-toast'

interface FeedbackForm {
  subject: string
  category: FeedbackCategory | ''
  rating: number
  message: string
  email: string
}

const FeedbackPage: React.FC = () => {
  const { isAuthenticated, user } = useAuth()

  const [formData, setFormData] = useState<FeedbackForm>({
    subject: '',
    category: '',
    rating: 0,
    message: '',
    email: ''
  })

  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errors, setErrors] = useState<Partial<Record<keyof FeedbackForm, string>>>({})

  // My feedbacks list (for MEMBER only)
  const [myFeedbacks, setMyFeedbacks] = useState<Feedback[]>([])
  const [isLoadingFeedbacks, setIsLoadingFeedbacks] = useState(false)

  // Fetch user's feedbacks if authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchMyFeedbacks()
    }
  }, [isAuthenticated])

  const fetchMyFeedbacks = async () => {
    setIsLoadingFeedbacks(true)
    try {
      const response = await feedbackService.getMyFeedbacks(1, 10)
      setMyFeedbacks(response.feedbacks)
    } catch (error: any) {
      console.error('Failed to fetch feedbacks:', error)
      // Don't show error toast - it's not critical
    } finally {
      setIsLoadingFeedbacks(false)
    }
  }

  const categories = feedbackService.getFeedbackCategories()

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    // Clear error when user starts typing
    if (errors[name as keyof FeedbackForm]) {
      setErrors(prev => ({ ...prev, [name]: '' }))
    }
  }

  const handleRatingClick = (rating: number) => {
    setFormData(prev => ({ ...prev, rating }))
    setErrors(prev => ({ ...prev, rating: '' }))
  }

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof FeedbackForm, string>> = {}

    if (!formData.subject.trim() || formData.subject.trim().length < 3) {
      newErrors.subject = 'หัวเรื่องต้องมีอย่างน้อย 3 ตัวอักษร'
    }

    if (!formData.category) {
      newErrors.category = 'กรุณาเลือกประเภทความคิดเห็น'
    }

    if (!formData.message.trim()) {
      newErrors.message = 'กรุณาแชร์ความคิดเห็นของคุณ'
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'ความคิดเห็นต้องมีอย่างน้อย 10 ตัวอักษร'
    }

    // Email is required for GUEST, optional for MEMBER
    if (!isAuthenticated) {
      if (!formData.email.trim()) {
        newErrors.email = 'กรุณากรอกอีเมลเพื่อติดต่อกลับ'
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = 'รูปแบบอีเมลไม่ถูกต้อง'
      }
    } else if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'รูปแบบอีเมลไม่ถูกต้อง'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setIsSubmitting(true)

    try {
      const requestData: CreateFeedbackRequest = {
        subject: formData.subject.trim(),
        category: formData.category as FeedbackCategory,
        message: formData.message.trim(),
        rating: formData.rating > 0 ? formData.rating : undefined,
        email: formData.email.trim() || undefined
      }

      await feedbackService.createFeedback(requestData)

      toast.success('ขอบคุณสำหรับความคิดเห็น!')
      setIsSubmitted(true)

      // Refresh feedbacks list if authenticated
      if (isAuthenticated) {
        fetchMyFeedbacks()
      }

      // Reset form after 3 seconds
      setTimeout(() => {
        setIsSubmitted(false)
        setFormData({
          subject: '',
          category: '',
          rating: 0,
          message: '',
          email: ''
        })
      }, 3000)
    } catch (error: any) {
      console.error('Feedback submission error:', error)
      const errorMessage = error?.response?.data?.detail || 'เกิดข้อผิดพลาดในการส่งความคิดเห็น กรุณาลองใหม่อีกครั้ง'
      toast.error(errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  const feedbackStructuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "แนะนำและติชม - ไทยเซ็กคิวริตี้ไดเรกทอรี่",
    "description": "แชร์ความคิดเห็น ข้อเสนอแนะ และประสบการณ์การใช้งานเว็บไซต์"
  }

  if (isSubmitted) {
    return (
      <>
        <SEO title="แนะนำและติชม" />
        <div className="bg-gradient-to-b from-green-50 to-white min-h-screen flex items-center justify-center py-12">
          <div className="container-wrapper">
            <div className="max-w-2xl mx-auto text-center">
              <div className="bg-white rounded-lg shadow-lg border border-green-200 p-12">
                <div className="flex justify-center mb-6">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircleIcon className="w-12 h-12 text-green-600" />
                  </div>
                </div>
                <h2 className="text-3xl font-bold text-neutral-900 mb-4">
                  ขอบคุณสำหรับความคิดเห็น!
                </h2>
                <p className="text-neutral-700 text-lg mb-2">
                  เราได้รับข้อความของคุณแล้ว
                </p>
                <p className="text-neutral-600">
                  ทีมงานจะนำไปพัฒนาและปรับปรุงบริการให้ดียิ่งขึ้น
                </p>
                {formData.rating > 0 && (
                  <div className="flex justify-center mt-6">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <StarIconSolid
                        key={star}
                        className={`w-8 h-8 ${
                          star <= formData.rating ? 'text-yellow-400' : 'text-neutral-300'
                        }`}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <SEO
        title="แนะนำและติชม"
        description="แชร์ความคิดเห็น ข้อเสนอแนะ และประสบการณ์การใช้งานไทยเซ็กคิวริตี้ไดเรกทอรี่ - เราให้ความสำคัญกับความคิดเห็นของคุณ"
        keywords="แนะนำติชม, feedback, ข้อเสนอแนะ, ความคิดเห็น, ประสบการณ์ผู้ใช้"
      />

      {/* Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(feedbackStructuredData) }}
      />

      <div className="bg-gradient-to-b from-primary-50 to-white min-h-screen">
        {/* Hero Section */}
        <div className="bg-primary-600 text-white py-16">
          <div className="container-wrapper">
            <div className="max-w-3xl mx-auto text-center">
              <div className="flex justify-center mb-6">
                <ChatBubbleLeftRightIcon className="w-16 h-16" />
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                แนะนำและติชม
              </h1>
              <p className="text-xl text-primary-100">
                ความคิดเห็นของคุณมีค่า ช่วยเราพัฒนาให้ดียิ่งขึ้น
              </p>
            </div>
          </div>
        </div>

        {/* Form Section */}
        <div className="container-wrapper py-12">
          <div className="max-w-3xl mx-auto">
            {/* Introduction */}
            <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6 mb-8">
              <p className="text-neutral-700 leading-relaxed">
                เราให้ความสำคัญกับทุกความคิดเห็นจากผู้ใช้งาน
                ไม่ว่าจะเป็นข้อเสนอแนะ คำชื่นชม หรือข้อร้องเรียน
                ทั้งหมดจะถูกนำไปพัฒนาและปรับปรุงเว็บไซต์ให้ตอบโจทย์ความต้องการของคุณมากขึ้น
              </p>
            </div>

            {/* Form */}
            <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Subject */}
                <div>
                  <label htmlFor="subject" className="block text-sm font-semibold text-neutral-900 mb-2">
                    หัวเรื่อง <span className="text-primary-600">*</span>
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder="สรุปความคิดเห็นของคุณในหัวเรื่อง"
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors ${
                      errors.subject ? 'border-red-500' : 'border-neutral-300'
                    }`}
                  />
                  {errors.subject && (
                    <p className="mt-1 text-sm text-red-600">{errors.subject}</p>
                  )}
                </div>

                {/* Category */}
                <div>
                  <label htmlFor="category" className="block text-sm font-semibold text-neutral-900 mb-2">
                    ประเภทความคิดเห็น <span className="text-primary-600">*</span>
                  </label>
                  <select
                    id="category"
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors ${
                      errors.category ? 'border-red-500' : 'border-neutral-300'
                    }`}
                  >
                    <option value="">เลือกประเภทความคิดเห็น</option>
                    {categories.map(cat => (
                      <option key={cat.value} value={cat.value}>
                        {cat.emoji} {cat.label}
                      </option>
                    ))}
                  </select>
                  {errors.category && (
                    <p className="mt-1 text-sm text-red-600">{errors.category}</p>
                  )}
                </div>

                {/* Rating */}
                <div>
                  <label className="block text-sm font-semibold text-neutral-900 mb-2">
                    ความพึงพอใจโดยรวม
                  </label>
                  <div className="flex items-center gap-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => handleRatingClick(star)}
                        className="focus:outline-none transition-transform hover:scale-110"
                      >
                        {star <= formData.rating ? (
                          <StarIconSolid className="w-10 h-10 text-yellow-400" />
                        ) : (
                          <StarIcon className="w-10 h-10 text-neutral-300 hover:text-yellow-200" />
                        )}
                      </button>
                    ))}
                    {formData.rating > 0 && (
                      <span className="ml-3 text-sm font-medium text-neutral-700">
                        {formData.rating === 5 && '😍 ยอดเยี่ยม'}
                        {formData.rating === 4 && '😊 ดีมาก'}
                        {formData.rating === 3 && '🙂 ดี'}
                        {formData.rating === 2 && '😐 พอใช้'}
                        {formData.rating === 1 && '😞 ควรปรับปรุง'}
                      </span>
                    )}
                  </div>
                  {errors.rating && (
                    <p className="mt-1 text-sm text-red-600">{errors.rating}</p>
                  )}
                </div>

                {/* Message */}
                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-neutral-900 mb-2">
                    ความคิดเห็นของคุณ <span className="text-primary-600">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={6}
                    placeholder="แชร์ประสบการณ์ ข้อเสนอแนะ หรือสิ่งที่อยากให้ปรับปรุง..."
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors resize-none ${
                      errors.message ? 'border-red-500' : 'border-neutral-300'
                    }`}
                  />
                  {errors.message && (
                    <p className="mt-1 text-sm text-red-600">{errors.message}</p>
                  )}
                  <p className="mt-1 text-xs text-neutral-500">
                    ขั้นต่ำ 10 ตัวอักษร ({formData.message.length}/10)
                  </p>
                </div>

                {/* Email (Required for GUEST, Optional for MEMBER) */}
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-neutral-900 mb-2">
                    อีเมลของคุณ {!isAuthenticated && <span className="text-primary-600">*</span>}
                    {isAuthenticated && <span className="text-neutral-500 text-xs">(ถ้าต้องการให้เราติดต่อกลับ)</span>}
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder={isAuthenticated ? 'อีเมลสำหรับติดต่อกลับ (ถ้าต้องการ)' : 'your.email@example.com'}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors ${
                      errors.email ? 'border-red-500' : 'border-neutral-300'
                    }`}
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                  )}
                </div>

                {/* Submit Button */}
                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`w-full px-6 py-3.5 bg-primary-600 text-white font-semibold rounded-lg hover:bg-primary-700 transition-colors duration-200 shadow-md ${
                      isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    {isSubmitting ? 'กำลังส่ง...' : 'ส่งความคิดเห็น'}
                  </button>
                </div>

                {/* Privacy Note */}
                <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-4">
                  <p className="text-xs text-neutral-600 leading-relaxed">
                    ข้อมูลที่คุณส่งมาจะถูกใช้เพื่อปรับปรุงบริการเท่านั้น
                    เราจะไม่เปิดเผยข้อมูลส่วนตัวของคุณให้บุคคลที่สาม
                    อ่านเพิ่มเติมที่ <a href="/privacy" className="text-primary-600 hover:underline">นโยบายความเป็นส่วนตัว</a>
                  </p>
                </div>
              </form>
            </div>

            {/* My Feedbacks Section (MEMBER only) */}
            {isAuthenticated && (
              <div className="mt-12 bg-white rounded-lg shadow-sm border border-neutral-200 p-8">
                <h3 className="text-xl font-bold text-neutral-900 mb-4">
                  ความคิดเห็นของคุณ
                </h3>
                {isLoadingFeedbacks ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                    <p className="mt-2 text-sm text-neutral-500">กำลังโหลด...</p>
                  </div>
                ) : myFeedbacks.length === 0 ? (
                  <div className="text-center py-8 text-neutral-500">
                    <p>คุณยังไม่มีความคิดเห็นที่ส่งมา</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {myFeedbacks.map((feedback) => {
                      const statusDisplay = feedbackService.getStatusDisplay(feedback.status)
                      return (
                        <div
                          key={feedback.id}
                          className="border border-neutral-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                        >
                          <div className="flex items-start justify-between mb-2">
                            <h4 className="font-semibold text-neutral-900">{feedback.subject}</h4>
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusDisplay.bgColor} ${statusDisplay.textColor}`}>
                              {statusDisplay.text}
                            </span>
                          </div>
                          <p className="text-sm text-neutral-600 mb-2">
                            {feedbackService.getCategoryDisplay(feedback.category)}
                          </p>
                          <p className="text-xs text-neutral-500">
                            ส่งเมื่อ: {feedbackService.formatDate(feedback.createdAt)}
                          </p>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Popular Suggestions */}
            <div className="mt-12 bg-blue-50 rounded-lg border border-blue-200 p-6">
              <h3 className="text-lg font-bold text-blue-900 mb-4">
                💡 ข้อเสนอแนะที่เราได้รับบ่อย
              </h3>
              <div className="space-y-3 text-sm text-blue-800">
                <div className="flex items-start gap-2">
                  <span>✅</span>
                  <p>เพิ่มฟีเจอร์บันทึกบริษัทที่สนใจ (กำลังพัฒนา)</p>
                </div>
                <div className="flex items-start gap-2">
                  <span>✅</span>
                  <p>ระบบเปรียบเทียบบริษัท side-by-side (อยู่ใน roadmap)</p>
                </div>
                <div className="flex items-start gap-2">
                  <span>✅</span>
                  <p>แอปพลิเคชันมือถือ iOS/Android (วางแผนสำหรับ Phase 3)</p>
                </div>
                <div className="flex items-start gap-2">
                  <span>✅</span>
                  <p>ระบบรีวิวและคะแนนจากผู้ใช้จริง (วางแผนสำหรับ Phase 2)</p>
                </div>
              </div>
            </div>

            {/* Thank You Message */}
            <div className="mt-8 text-center">
              <p className="text-neutral-600 text-sm">
                ขอบคุณที่ใช้เวลาแชร์ความคิดเห็น เราให้ความสำคัญกับทุกความคิดเห็น
              </p>
              <p className="text-neutral-600 text-sm mt-1">
                และจะนำไปพัฒนาให้เว็บไซต์ดียิ่งขึ้น 🙏
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default FeedbackPage
